'use client'

import React, { useRef, useState, useEffect, useMemo } from 'react';
import dynamic from "next/dynamic";
import { useParams } from 'next/navigation'
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { Tabs, TabList, TabPanels, Tab, TabPanel } from '@chakra-ui/react'
import GameMainFeatures from './GameMainFeatures';
import GameBasicInfo from './GameBasicInfo';
import GameFirmInfo from './GameFirmInfo';
import GameActions from './GameActions';
import GamePriceInfo from './GamePriceInfo';
import GameIntro from './GameIntro';
import GameCommentBlock from './GameCommentBlock'
import { useGlobal, useUser } from 'src/context';
import { GAME_CARD_IMAGE_PLACEHOLDER_SRC, trackEvent } from 'utils';

const SimilarGamesContainer = dynamic(() => import('./SimilarGames'));

const RelatedGamesContainer = dynamic(() => import('./RelatedGames'));

const ReviewForm = dynamic(() => import('components/common/ReviewForm'));

// const GameCommentBlock = dynamic(() => import('./GameCommentBlock'));

const BANNER_RATION = 628 / 1200 ; // FACEBOOK 分享連結的最佳尺寸

const GamePageLayout = (props) => {
  const { gameDetail } = props;
  const {
    title,
    content,
    mainImgBannerUrl, 
    mainImgBannerAlt,
    mainImgSquareUrl,
    mainImgSquareAlt,
    isGameOver,
    priceTable,
    unitPrice,
    firmsList,
    similarGames,
    relatedGames,
    identicalGames,
    challenges
  } = gameDetail;

  const [isReviewDialogOpen, setIsReviewDialogOpen] = useState(false);

  const { gameId } = useParams()

  const { onOpenLogin } = useGlobal();

  const { isLogin, isLoadingUser } = useUser();

  const bannerContainerRef = useRef(null);

  useEffect(() => {
    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, []);

  useEffect(() => {
    if (window.fbq) {
      fbq('track', 'ViewContent', {
        content_category: 'GamePage',
        content_name: title,
        content_ids: [gameId],
        content_type: 'game',
      });
    }
  }, [gameId]);


  const handleResize = () => {
    if (!bannerContainerRef.current) return;
    
    bannerContainerRef.current.style.height = `${bannerContainerRef.current.clientWidth * BANNER_RATION}px`;
  };

  const handleReviewDialogOpen = () => {
    trackEvent({
      category: 'Review_edit--GamePage',
      action: 'Click',
      label: gameId,
    });

    if (isLogin) {
      setIsReviewDialogOpen(true);
    } else {
      if (!isLoadingUser) onOpenLogin();
    }
  };

  const handleReviewDialogClose = () => {
    setIsReviewDialogOpen(false);
  };

  return (
    <>
      <div>
        <section className='mb-4 flex flex-col lg:flex-row gap-x-3 bg-white'>
          <div 
            ref={bannerContainerRef}
            className='w-full inline-flex lg:flex-grow overflow-hidden' 
          >
            <LazyLoadImage
              src={mainImgBannerUrl || mainImgSquareUrl || GAME_CARD_IMAGE_PLACEHOLDER_SRC}
              alt={mainImgBannerAlt || mainImgSquareAlt || title}
              className='w-full object-cover'
            />
          </div>

          <div className='w-full lg:max-w-[20rem] px-2 lg:px-0'>
            <GameMainFeatures 
              {...gameDetail} 
              onOpenReview={handleReviewDialogOpen}
            />
          </div>
        </section>

        <div className='flex flex-col lg:flex-row gap-3'>
          <div className='w-full lg:flex-grow px-2 lg:px-0 overflow-hidden'>
            <Tabs isFitted>
              <TabList>
                <Tab>遊戲資訊</Tab>
                <Tab>玩家評價</Tab>
                <Tab>相關作品</Tab>
              </TabList>

              <TabPanels>
                <TabPanel px={0}>
                  <div className='flex flex-col gap-3'>
                    <div className=''>
                      <GameBasicInfo {...gameDetail} />
                    </div>
                    {!isGameOver && (
                      <div className=''>
                        <GamePriceInfo
                          gameId={gameId}
                          title={title}
                          unitPrice={unitPrice}
                          priceTable={priceTable}
                        />
                      </div>
                    )}
                    <GameIntro content={content} />
                </div>
                </TabPanel>
                <TabPanel px={0}>
                  <GameCommentBlock 
                    gameId={gameId} 
                    title={title} 
                    isEditingReview={isReviewDialogOpen}
                    onStartToReview={handleReviewDialogOpen}
                  />
                </TabPanel>
                <TabPanel px={0}>
                  <RelatedGamesContainer
                    challenges={challenges}
                    relatedGames={relatedGames}
                    identicalGames={identicalGames}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </div>

          <div className='w-full lg:max-w-[20rem] px-2 lg:px-0 flex flex-col gap-y-3 lg:gap-y-6'>
            <GameFirmInfo firmsList={firmsList} />
            <SimilarGamesContainer gamesList={similarGames} />
          </div>
        </div>
      </div>
      {isReviewDialogOpen && (
        <ReviewForm
          isOpen={isReviewDialogOpen}
          gameId={gameId}
          title={title}
          onClose={handleReviewDialogClose}
        />
      )}
    </>
  );
}

export default GamePageLayout;